import { Link, router } from '@inertiajs/react';
import { ArrowLeft, X } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import { FileUploader } from '@/components/file-uploader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppLayout from '@/layouts/app-layout';


interface ShareFile {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  created_at: string;
  url: string;
}

interface EditShareProps {
  share: {
    id: number;
    title: string;
    description: string;
    slug: string;
    expires_at: string | null;
    view_count: number;
    created_at: string;
    public_url: string;
    files: ShareFile[];
  };
  errors?: string[];
}

export default function EditShare({ share, errors }: EditShareProps) {
  const [formData, setFormData] = useState({
    title: share.title,
    description: share.description || '',
    expires_at: share.expires_at ? new Date(share.expires_at).toISOString().slice(0, 16) : ''
  });
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [existingFiles, setExistingFiles] = useState<ShareFile[]>(share.files);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    router.put(`/shares/${share.id}`, {
      share: formData,
      file_ids: fileIds
    }, {
      onFinish: () => setIsSubmitting(false)
    });
  };

  const handleFilesUploaded = (newFileIds: string[]) => {
    setFileIds(prev => [...prev, ...newFileIds]);
  };

  const removeExistingFile = (fileId: string) => {
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
    // You might want to track removed files to delete them on the backend
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link href={`/shares/${share.id}`} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Share
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Edit Share</CardTitle>
            <CardDescription>
              Update share details and manage files
            </CardDescription>
          </CardHeader>
          <CardContent>
            {errors && errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 dark:text-red-200 font-semibold mb-2">
                  Please fix the following errors:
                </h3>
                <ul className="list-disc list-inside text-red-700 dark:text-red-300">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter a title for your share"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add an optional description"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="expires_at">Expiration Date (Optional)</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank for no expiration
                </p>
              </div>

              <div>
                <Label>Current Files</Label>
                {existingFiles.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {existingFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{file.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.byte_size)}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExistingFile(file.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-2">No files attached</p>
                )}
              </div>

              <div>
                <Label>Add More Files</Label>
                <div className="mt-2">
                  <FileUploader onFilesUploaded={handleFilesUploaded} />
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Link href={`/shares/${share.id}`}>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Share'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}